<template>
    <v-row
        no-gutters
        class="pl-9 pt-8"
    >
        <v-col
            cols="12"
            class="pr-10"
            style="word-break: normal"
        >
            <v-row>
                <v-col cols="12">
                    <div class="title">{{ $t('Flextime period') }}</div>
                </v-col>
                <v-col cols="12">
                    <ExpansionPanels
                        :items="visibleAgreements"
                        :title="formatTitle"
                        :errors="errors"
                        :value="openExpansionPanels"
                        @change="openExpansionPanels = $event"
                    >
                        <template v-slot:actions="{ item }">
                            <icon-button @click="onDelete(item)">
                                mdi-delete
                            </icon-button>
                        </template>
                        <template v-slot:default="{ item, index }">
                            <MainFlextimeAgreementContent
                                :key="keyHack"
                                :item="item"
                                :index="index"
                                :errors="errors"
                                @update-errors="errors = $event"
                                @update-item-start-date="item.date_from = $event"
                                @update-item-date-until="item.date_until = $event"
                                @update-item-working-hour="item._data[$event.key] = $event.value"
                                @update-item-work-holidays="item.work_holidays = $event"
                            >
                                <template #insert>
                                    <div class="title">{{ $t('Confirmation period') }}</div>
                                    <v-radio-group class="mt-4 mb-4" row hide-details
                                                   v-model="item.type"
                                                   :error-messages="errors[`${index}.type`]">
                                        <v-radio :label="$t('Weekly')" color="primary" value="weekly">
                                            <template v-slot:label>
                                                <div class="body-2">
                                                    {{ $t('Weekly') }}
                                                </div>
                                            </template>
                                        </v-radio>
                                        <v-radio
                                            color="primary"
                                            value="monthly"
                                        >
                                            <template v-slot:label>
                                                <div class="body-2">
                                                    {{ $t('Monthly') }}
                                                </div>
                                            </template>
                                        </v-radio>
                                    </v-radio-group>
                                </template>
                            </MainFlextimeAgreementContent>
                        </template>
                    </ExpansionPanels>
                </v-col>
            </v-row>
        </v-col>
    </v-row>
</template>

<script>
import AppStoreDialogSidebar from "@/components/AppStore/AppStoreDialogSidebar";
import {$t} from "@/i18n";
import appStoreDialog from "@/store/modules/AppStoreDialog/appStoreDialog";
import {computed, onMounted, reactive, ref, watch} from "@vue/composition-api";
import errorDialog from "@/views/ErrorDialog/errorDialog";
import ApiAppStoreFlextime from "@/api/app_store/flextime";
import Loader from "@/utility/loader";
import FeatureModule from "@/store/modules/feature";
import {
    formatDate,
    formatSysDate,
    formatTimeDuration,
    parseSysDate,
    parseSysDateTime,
    parseTimeDuration,
    weekdayNames
} from "@/plugins/formatting";
import ValidationModule from "@/modules/validation/validation"
import ExpansionPanels from "@/components/ExpansionPanels/Index";
import FeedbackNotificationModule from "@/store/modules/feedback_notification";
import Vue from "vue";
import MainFlextimeAgreementContent from "@/components/Flextime/Agreement/MainFlextimeAgreementContent.vue";

export default {
    name: "FlextimeDialogSettingsTab",
    components: {
        AppStoreDialogSidebar,
        ExpansionPanels,
        MainFlextimeAgreementContent
    },
    props: {
        isSelected: {
            type: Boolean,
            default: false
        }
    },
    setup(props, ctx) {
        const loader = reactive(new Loader());
        const errors = ref({});

        const {showFeedbackNotification} = FeedbackNotificationModule();
        const {appStoreDialogGetters, appStoreDialogActions} = appStoreDialog();
        const agreements = ref([]);
        const openExpansionPanels = ref([0]);
        const featureModule = FeatureModule();
        const workingHoursType = ref('custom');
        // When adding new agreement then custom is selected, ugly hack
        const keyHack = ref(false)

        const selectedAccount = computed(() => {
            return appStoreDialogGetters.getSelectedCompany();
        });

        const addNewAgreement = () => {
            agreements.value.unshift({
                company_id: selectedAccount.value.id,
                cost_price: 0,
                created_at: null,
                is_deleted: 0,
                date_from: null,
                date_until: null,
                id: null,
                person_id: null,
                sales_price: 0,
                type: "weekly",
                updated_at: null,
                work_holidays: 0,
                workingHoursType: 'standard',
                _data: {
                    'monday': 28800,
                    'tuesday': 28800,
                    'wednesday': 28800,
                    'thursday': 28800,
                    'friday': 28800,
                    'saturday': 0,
                    'sunday': 0
                }
            })

            openExpansionPanels.value = [0]
            keyHack.value = !keyHack.value
        }

        const fetchData = async () => {
            try {
                let data = await ApiAppStoreFlextime.getCompanyFlextimeSettings(selectedAccount.value.id)
                agreements.value = data.map(x => {
                    if (!x._data) {
                        x._data = {}
                    }

                    if (!x._data.monday) {
                        x._data.monday = 0
                    }

                    if (!x._data.tuesday) {
                        x._data.tuesday = 0
                    }

                    if (!x._data.wednesday) {
                        x._data.wednesday = 0
                    }

                    if (!x._data.thursday) {
                        x._data.thursday = 0
                    }

                    if (!x._data.friday) {
                        x._data.friday = 0
                    }

                    if (!x._data.saturday) {
                        x._data.saturday = 0
                    }

                    if (!x._data.sunday) {
                        x._data.sunday = 0
                    }

                    x.is_deleted = 0;
                    x.date_from = formatSysDate(parseSysDateTime(x.date_from))
                    x.date_until = x.date_until ? formatSysDate(parseSysDateTime(x.date_until)) : null
                    return x
                })
                openExpansionPanels.value = agreements.value.length ? [0] : []
            } catch (e) {
                errorDialog.showError(e)
            }
        }

        onMounted(() => {
            fetchData()
        })

        const formatTitle = (item) => {
            let output = ""
            if (item.type === 'monthly') {
                output += $t('Monthly')
            } else if (item.type === 'weekly') {
                output += $t('Weekly')
            }

            if (item.date_from) {
                output += " " + formatDate(parseSysDate(item.date_from)) + ' - '
            }

            if (item.date_until) {
                output += " " + formatDate(parseSysDate(item.date_until))
            } else {
                output += "..."
            }

            return output
        }

        const save = async () => {
            await loader.withLoader(async () => {
                try {
                    const accountFeature = appStoreDialogGetters.getCurrentFeature()
                    await featureModule.execAppAction({
                        account_id: selectedAccount.value.id,
                        feature_id: accountFeature.feature_id,
                        action: "save_settings",
                        data: {
                            agreements: agreements.value
                        }
                    })
                    showFeedbackNotification($t('Settings saved'))
                    await fetchData()
                } catch (e) {
                    errors.value = ValidationModule.parseErrors(e)
                    if (!ValidationModule.hasErrors(errors.value)) {
                        errorDialog.showError(e)
                    }
                }
            })
        }

        const loading = computed(() => loader.isLoading)

        watch(() => props.isSelected, () => {
            if (props.isSelected) {
                appStoreDialogActions.setFooterActions([
                    {
                        text: $t("Add agreement"),
                        class: "mr-auto primary--text ml-n3",
                        icon: 'mdi-plus',
                        isTextStyle: true,
                        click: addNewAgreement,
                        loading: loading
                    },
                    {
                        text: $t("Save"),
                        class: "primary",
                        click: save,
                        loading: loading
                    }
                ]);
            }
        })

        const onDelete = item => {
            item.is_deleted = 1
            errors.value = {}
        }

        const firstDayOfWeek = computed(function () {
            return Vue.formatting.first_week_day
        })

        const workingHourItems = computed(() => {
            let items = [
                {
                    key: 'monday',
                    label: weekdayNames()[1]
                },
                {
                    key: 'tuesday',
                    label: weekdayNames()[2]
                },
                {
                    key: 'wednesday',
                    label: weekdayNames()[3]
                },
                {
                    key: 'thursday',
                    label: weekdayNames()[4]
                },
                {
                    key: 'friday',
                    label: weekdayNames()[5]
                },
                {
                    key: 'saturday',
                    label: weekdayNames()[6]
                }
            ]

            let sunday = {
                key: 'sunday',
                label: weekdayNames()[0]
            }

            if (firstDayOfWeek.value % 7 === 0) {
                items.unshift(sunday)
            } else {
                items.push(sunday)
            }

            return items
        })

        const visibleAgreements = computed(() => agreements.value.filter(x => !x.is_deleted))

        const getWorkingHourValue = (item, key) => {
            if (!(item._data && item._data[key])) {
                return formatTimeDuration(0, '', true, true)
            }

            return formatTimeDuration(item._data[key], '', true, true)
        }

        const getTotalWorkingHourDuration = (item) => {
            if (!item._data) {
                return 0
            }

            let total = 0

            total += item._data.monday
            total += item._data.tuesday
            total += item._data.wednesday
            total += item._data.thursday
            total += item._data.friday
            total += item._data.saturday
            total += item._data.sunday

            return formatTimeDuration(total, '', true, true)
        }

        return {
            formatTitle,
            addNewAgreement,
            visibleAgreements,
            parseTimeDuration,
            formatTimeDuration,
            openExpansionPanels,
            errors,
            onDelete,
            workingHoursType,
            workingHourItems,
            getWorkingHourValue,
            getTotalWorkingHourDuration,
            keyHack
        };
    }
};
</script>
